import React, { useEffect, useMemo, useState } from "react";

import Pager from "./Pager";
import useInstituteData, { percentOf, isPass, fmtDate, downloadCsv } from "../Useinstitutedata";
import InstituteSidebar from "./Institutesidebar";
import InstituteTopbar from "./Institutetopbar";

const PAGE_SIZE = 10;

export default function InstituteResults() {
  const { students, scores, loading, error } = useInstituteData();
  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState("All");
  const [resultFilter, setResultFilter] = useState("All");
  const [page, setPage] = useState(1);

  const nameOf = (id) =>
  students.find((student) => String(student.id) === String(id))
    ?.fullName || "—";

const rows = useMemo(
  () =>
    scores
      .map((result) => {
        const studentName =
          result.student_name ||
          nameOf(result.student_id);

        const resultStatus = result.result_status || "Pending";

        return {
          ...result,
          studentName,
          courseName: result.exam_name || "—",
          examDate: result.published_at,
          result: resultStatus,
          total: result.total_questions,
          percent:
            result.total_questions > 0
              ? Math.round(
                  (result.correct_answers / result.total_questions) * 100
                )
              : 0,
          pass: isPass(resultStatus),
        };
      })
      .sort((a, b) =>
        (b.published_at || "").localeCompare(a.published_at || "")
      ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [scores, students]
);
  const courseNames = useMemo(() => Array.from(new Set(rows.map((r) => r.courseName))).filter(Boolean), [rows]);

  const filtered = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    return (
      (!q || r.studentName.toLowerCase().includes(q)) &&
      (courseFilter === "All" || r.courseName === courseFilter) &&
      (resultFilter === "All" || (resultFilter === "Pass" ? r.pass : !r.pass))
    );
  });

  useEffect(() => setPage(1), [search, courseFilter, resultFilter]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const avg = filtered.length ? Math.round(filtered.reduce((a, r) => a + r.percent, 0) / filtered.length) : null;
  const cards = [
    { label: "Results", value: filtered.length, icon: "bi-journal-check" },
    { label: "Passed", value: filtered.filter((r) => r.pass).length, icon: "bi-check-circle" },
    { label: "Failed", value: filtered.filter((r) => !r.pass).length, icon: "bi-x-circle" },
    { label: "Average Score", value: avg == null ? "—" : `${avg}%`, icon: "bi-graph-up" },
  ];

  const exportCsv = () =>
    downloadCsv("institute-results.csv", [
      ["Student", "Course", "Exam date", "Score", "Total", "Percent", "Result"],
      ...filtered.map((r) => [r.studentName, r.courseName, r.examDate || "", r.score, r.total, r.percent, r.result]),
    ]);

  return (
    <div className="admin-page-full d-flex">
      <InstituteSidebar />
      <div className="admin-content">
        <InstituteTopbar />

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h2 className="h5 fw-bold mb-0">Results</h2>
          <button className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={exportCsv} disabled={filtered.length === 0}>
            <i className="bi bi-download" /> Export CSV
          </button>
        </div>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="row g-3 mb-3">
          {cards.map((c) => (
            <div className="col-6 col-md-3" key={c.label}>
              <div className="admin-stat-card">
                <div className="admin-stat-icon"><i className={`bi ${c.icon}`} /></div>
                <div className="admin-stat-value">{c.value}</div>
                <div className="admin-stat-label">{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-card p-3 mb-3">
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                <input className="form-control" placeholder="Search by student name..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="col-6 col-md-4">
              <select className="form-select" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                <option value="All">All Courses</option>
                {courseNames.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <select className="form-select" value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
                <option value="All">All Results</option>
                <option>Pass</option>
                <option>Fail</option>
              </select>
            </div>
          </div>
        </div>

        <div className="admin-card p-3 p-md-4">
          {loading && <p className="text-muted mb-0">Loading results...</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-muted small mb-0">
              {rows.length === 0 ? "No exam has been attempted by your students yet." : "No results match your filters."}
            </p>
          )}
          {!loading && filtered.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead><tr><th>#</th><th>Student</th><th>Course</th><th>Exam Date</th><th>Score</th><th>Result</th></tr></thead>
                  <tbody>
                    {paged.map((r, i) => (
                      <tr key={r.id}>
                        <td className="text-muted">{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td className="fw-semibold">{r.studentName}</td>
                        <td>{r.courseName}</td>
                        <td className="text-muted small">{fmtDate(r.examDate)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="inst-bar"><div style={{ width: `${r.percent}%`, background: r.pass ? "var(--oes-green)" : "var(--oes-red)" }} /></div>
                            <span className="small fw-semibold text-nowrap">{r.score}/{r.total} ({r.percent}%)</span>
                          </div>
                        </td>
                        <td>
                          <span className={`admin-status-pill ${r.pass ? "active" : "inactive"}`}>{r.result}</span>
                          {r.autoSubmitted && <span className="text-muted small ms-2" title={r.autoSubmitReason || "Auto-submitted"}>auto-submitted</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}